import React, { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const TEAM_SIZES = [
  { value: "none", label: "No direct reports" },
  { value: "1-3", label: "1–3 people" },
  { value: "4-10", label: "4–10 people" },
  { value: "10+", label: "10+ people" },
];

const FIELDS = [
  {
    key: "job_title",
    label: "What you do",
    placeholder: "e.g. Director of Marketing, Software Engineer, Founder…",
    hint: "Job title or role.",
    type: "text",
  },
  {
    key: "industry",
    label: "Where you work",
    placeholder: "e.g. B2B SaaS, healthcare, nonprofit, agency…",
    hint: "Industry or company — your call.",
    type: "text",
  },
  {
    key: "team_size",
    label: "Team you manage",
    type: "select",
  },
  {
    key: "current_focus",
    label: "What you're working through right now",
    placeholder: "One sentence. Optional.",
    hint: "Big project, tough situation, anything top-of-mind.",
    type: "textarea",
  },
  {
    key: "hobbies",
    label: "Things you like outside work",
    placeholder: "e.g. trail running, baking, vinyl records, building Legos…",
    type: "text",
  },
  {
    key: "music",
    label: "Music you're into",
    placeholder: "e.g. indie rock, jazz, country, hip-hop…",
    type: "text",
  },
];

/**
 * ProfileForm — fields are all optional. Caller decides what to do after save.
 * Props:
 *   initial: current profile object (may be empty)
 *   onSaved(profile): called after a successful PUT
 *   onSkip(): optional — only shown when present (welcome modal)
 *   submitLabel: customise "Save" wording
 *   compact: tighter spacing for modal use
 */
export default function ProfileForm({
  initial = {},
  onSaved,
  onSkip,
  submitLabel = "Save",
  compact = false,
}) {
  const [values, setValues] = useState(() => ({
    job_title: initial.job_title || "",
    industry: initial.industry || "",
    team_size: initial.team_size || "",
    current_focus: initial.current_focus || "",
    hobbies: initial.hobbies || "",
    music: initial.music || "",
  }));
  const [saving, setSaving] = useState(false);

  const update = (key, val) => setValues((v) => ({ ...v, [key]: val }));

  const save = async (e) => {
    e?.preventDefault();
    setSaving(true);
    try {
      // Send all fields (backend strips empties)
      const { data } = await api.put("/profile", values);
      toast.success("Profile saved");
      onSaved?.(data.profile || {});
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const skip = async () => {
    setSaving(true);
    try {
      await api.post("/profile/skip");
      onSkip?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      onSubmit={save}
      data-testid="profile-form"
      className={compact ? "space-y-4" : "space-y-6"}
    >
      {FIELDS.map((f) => (
        <div key={f.key} className="space-y-1.5">
          <label
            htmlFor={`profile-${f.key}`}
            className="text-sm text-[#3D3833] font-medium"
          >
            {f.label}{" "}
            <span className="text-[#A39B91] font-normal text-xs">(optional)</span>
          </label>
          {f.type === "textarea" ? (
            <textarea
              id={`profile-${f.key}`}
              data-testid={`profile-${f.key}`}
              value={values[f.key]}
              onChange={(e) => update(f.key, e.target.value)}
              placeholder={f.placeholder}
              rows={2}
              className="w-full px-3.5 py-2.5 rounded-xl border border-[#E6E2D8] bg-white focus:outline-none focus:ring-2 focus:ring-[#D4A574]/40 focus:border-[#D4A574]/60 text-sm text-[#2D2926] resize-none"
            />
          ) : f.type === "select" ? (
            <select
              id={`profile-${f.key}`}
              data-testid={`profile-${f.key}`}
              value={values[f.key]}
              onChange={(e) => update(f.key, e.target.value)}
              className="w-full px-3.5 py-2.5 rounded-xl border border-[#E6E2D8] bg-white focus:outline-none focus:ring-2 focus:ring-[#D4A574]/40 focus:border-[#D4A574]/60 text-sm text-[#2D2926]"
            >
              <option value="">Pick if you'd like…</option>
              {TEAM_SIZES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          ) : (
            <input
              id={`profile-${f.key}`}
              data-testid={`profile-${f.key}`}
              type="text"
              value={values[f.key]}
              onChange={(e) => update(f.key, e.target.value)}
              placeholder={f.placeholder}
              className="w-full px-3.5 py-2.5 rounded-xl border border-[#E6E2D8] bg-white focus:outline-none focus:ring-2 focus:ring-[#D4A574]/40 focus:border-[#D4A574]/60 text-sm text-[#2D2926]"
            />
          )}
          {f.hint && (
            <p className="text-xs text-[#9A938A]">{f.hint}</p>
          )}
        </div>
      ))}

      <div className="flex items-center justify-end gap-3 pt-2">
        {onSkip && (
          <button
            type="button"
            data-testid="profile-skip-btn"
            onClick={skip}
            disabled={saving}
            className="text-sm text-[#6B655D] hover:text-[#2D2926] px-4 py-2.5 rounded-full transition-colors disabled:opacity-50"
          >
            Skip for now
          </button>
        )}
        <button
          type="submit"
          data-testid="profile-save-btn"
          disabled={saving}
          className="bg-[#2D2926] text-white hover:bg-[#1A1715] rounded-full px-6 py-2.5 text-sm font-medium transition-colors disabled:opacity-50"
        >
          {saving ? "Saving…" : submitLabel}
        </button>
      </div>
    </form>
  );
}
